import React from "react";
import { motion } from "framer-motion";
import { Check, X } from "lucide-react";
import Chapter from "../site/Chapter";

const rows = [
  {
    label: "Output validation",
    ai: "AI guesses at fit / accuracy",
    a4: "Trained specialists review every output",
  },
  {
    label: "Quality",
    ai: "Templated, mass-produced results",
    a4: "Personalized, specialist-approved work",
  },
  {
    label: "Ownership",
    ai: "Your team juggles tools and chases outcomes",
    a4: "A dedicated ops team runs the workflow daily",
  },
  {
    label: "Coordination",
    ai: "Drops between tools, teams, and follow-up",
    a4: "One operating layer keeps everything in sync",
  },
  {
    label: "Quality control",
    ai: "Whatever the model returns ships",
    a4: "Human QC before anything reaches you",
  },
  {
    label: "What's measured",
    ai: "Activity (messages sent, records processed)",
    a4: "Outcomes (hires, resolutions, decisions made)",
  },
];

export default function HomeComparison() {
  return (
    <section
      id="comparison"
      data-testid="comparison-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <Chapter
          number="04"
          eyebrow="AI Tools vs. Human-Led AI Ops"
          title={
            <>
              AI alone creates activity.{" "}
              <span className="text-gradient-cyan">Human-led AI creates outcomes.</span>
            </>
          }
          subtitle="Buying AI tools doesn't fix operations. Running them — with trained specialists in the loop — does. Here's the difference in practice."
        />

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-800/70 border border-slate-800 rounded-2xl overflow-hidden">
          {/* AI Tools Alone */}
          <div className="bg-[#080F1E] p-8 sm:p-10">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-slate-800 text-slate-400 inline-flex items-center justify-center">
                <X size={18} />
              </div>
              <h3 className="font-display text-lg font-semibold text-slate-300">
                AI Tools Alone
              </h3>
            </div>
            <p className="mt-2 text-sm text-slate-500">
              More output. No accountability. Same operational chaos.
            </p>

            <ul className="mt-8 space-y-5">
              {rows.map((r) => (
                <li key={r.label} className="flex items-start gap-3">
                  <X size={16} className="text-slate-600 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[11px] tracking-wider uppercase text-slate-500 font-semibold">
                      {r.label}
                    </div>
                    <div className="text-sm text-slate-400 mt-0.5">{r.ai}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* A4 Assist */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative bg-gradient-to-br from-[#0B1A33] to-[#071326] p-8 sm:p-10 overflow-hidden"
          >
            <div className="absolute -top-24 -right-16 h-64 w-64 rounded-full bg-[#00E5FF]/10 blur-3xl" />
            <div className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-[#A3FF5E]/10 blur-3xl" />

            <div className="relative">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-[#00E5FF]/15 text-[#00E5FF] inline-flex items-center justify-center border border-[#00E5FF]/30">
                  <Check size={18} />
                </div>
                <h3 className="font-display text-lg font-semibold text-white">
                  A4 Assist · Human-Led AI Ops
                </h3>
                <span className="ml-auto text-[10px] tracking-[0.2em] uppercase font-bold text-[#A3FF5E] border border-[#A3FF5E]/30 bg-[#A3FF5E]/10 rounded-full px-2.5 py-1">
                  Recommended
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-400">
                AI does the volume. Specialists do the judgment. You get finished work.
              </p>

              <ul className="mt-8 space-y-5">
                {rows.map((r) => (
                  <li key={r.label} className="flex items-start gap-3">
                    <Check size={16} className="text-[#A3FF5E] mt-0.5 shrink-0" />
                    <div>
                      <div className="text-[11px] tracking-wider uppercase text-[#00E5FF] font-semibold">
                        {r.label}
                      </div>
                      <div className="text-sm text-white mt-0.5">{r.a4}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
